import React, { useState } from "react";
import { Mail, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { StatusChip } from "@/components/common";

const EXAMPLE_EMAIL = `Hi David,

Thanks for the workshop. Two updates from our side: we want to go from three to four cameras so we can cover the interview area properly, and we need Microsoft Teams integration for remote guests.

Also - our IT department asks whether the media network can be fully separated from the office network. And can you include LED lighting in the proposal?

Best,
Anne`;

export function ExtractDialog({ open, onOpenChange, opportunityId, onDone }) {
  const [text, setText] = useState("");
  const [source, setSource] = useState("email");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (text.trim().length < 10) return toast.error("Paste some text first");
    setBusy(true);
    try {
      const r = await api.post(`/opportunities/${opportunityId}/ai/extract`, { text, source });
      setResult(r.data);
      toast.success(`${r.data.added.length} new requirement${r.data.added.length === 1 ? "" : "s"} found`);
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const close = (v) => {
    if (!v) {
      setResult(null);
      setText("");
    }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-[620px]" data-testid="extract-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl">Update from e-mail or notes</DialogTitle>
          <DialogDescription>Paste the customer&apos;s message. The AI finds new requirements, changes, risks and questions and updates the opportunity memory.</DialogDescription>
        </DialogHeader>
        {!result ? (
          <div className="space-y-3">
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="h-9 w-[180px] rounded-[10px]" data-testid="extract-source-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="email">E-mail</SelectItem><SelectItem value="notes">Notes</SelectItem><SelectItem value="document">Document text</SelectItem></SelectContent>
            </Select>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={9} className="rounded-xl text-sm leading-6" placeholder="Paste e-mail or notes here..." data-testid="extract-textarea" />
            <div className="flex items-center justify-between">
              <Button variant="ghost" onClick={() => setText(EXAMPLE_EMAIL)} data-testid="extract-use-example-button">Use example e-mail</Button>
              <Button onClick={run} disabled={busy} className="gap-2 bg-primary text-white hover:bg-[#ff8e76]" data-testid="extract-run-button"><Sparkles className={`h-4 w-4 ${busy ? "animate-pulse" : ""}`} /> {busy ? "Reading..." : "Find updates"}</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 fade-in" data-testid="extract-result">
            <div className="ai-banner text-sm"><span className="font-semibold">Updates applied.</span> {result.added.length} new requirement{result.added.length === 1 ? "" : "s"}{result.changes?.length ? `, ${result.changes.length} change${result.changes.length === 1 ? "" : "s"}` : ""}{result.open_questions?.length ? `, ${result.open_questions.length} open question${result.open_questions.length === 1 ? "" : "s"}` : ""}.</div>
            {result.changes?.length > 0 && (
              <div>
                <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">Changes</div>
                <ul className="space-y-1 text-sm">{result.changes.map((c, i) => <li key={i} className="flex items-center gap-2"><span className="font-medium">{c.field}</span><span className="text-muted-foreground line-through">{c.from}</span><span>→</span><span className="font-medium">{c.to}</span></li>)}</ul>
              </div>
            )}
            {result.added.length > 0 && (
              <div>
                <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">New requirements</div>
                <ul className="space-y-1.5 text-sm">{result.added.map((r) => <li key={r.id} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-[hsl(var(--status-ok))]" /><span className="flex-1"><span className="font-medium">{r.title}</span> <span className="text-muted-foreground">– {r.detail}</span></span><StatusChip status={r.status} /></li>)}</ul>
              </div>
            )}
            {result.next_step && <div className="text-sm"><span className="text-muted-foreground">Suggested next step:</span> {result.next_step}</div>}
            <div className="flex justify-end"><Button onClick={() => close(false)} className="bg-primary text-white hover:bg-[#ff8e76]" data-testid="extract-done-button">Done</Button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
